import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, MapPin, Briefcase, Users, TrendingUp, BookOpen,CheckCircle,
  ArrowRight,Upload,Zap,Shield,Globe,Sparkles
} from 'lucide-react';
import SEO from '../components/SEO';
import { translations } from '../data/translations'; // adjust path
import NewsletterSubscription from '../components/NewsleterAndSalaryGuide';
import companiesData from '../data/companies.json'; // adjust path to your JSON file
import { useApi } from '../contexts/ApiContext';
import blogPostData from '../data/blog.json';
import { useNavigate } from 'react-router-dom';
import CVUploadModal from '../components/CVUploadModal';
import { useAuth } from '../contexts/AuthContext';
import ReactGA from 'react-ga4';

const Home = () => {
  const [lang, setLang] = useState('en'); // default to French
  const t = translations[lang];
  
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { jobs, loading, getJobs } = useApi();
  const companies = companiesData;
  const blogPosts = blogPostData;
  const [isCVModalOpen, setIsCVModalOpen] = useState(false);
  // On mount, refresh jobs if not loaded
  useEffect(() => {
    if (!jobs) {
      getJobs();
    }
  }, [jobs, getJobs]);
  
  const stats = [
    { label: 'Active Jobs', value: loading ? '...' : jobs?.length ?? 1500, icon: Briefcase },
    { label: 'Companies', value: companies.length + '+', icon: Users },
    { label: 'Blog Articles', value: blogPosts.length + '', icon: BookOpen }
  ];
  const categories = [
    { label: 'Students', filter: 'students', tags: ['Internship', 'Stage', 'Apprenticeship','postdoc'], icon: BookOpen },
    { label: 'Developer', filter: 'developer', tags: ['Developer', 'Frontend', 'Backend', 'Fullstack', 'coding', 'logiciel', 'cicd', 'python'], icon: Briefcase },
    { label: 'DevOps', filter: 'devops', tags: ['architect','devops','cicd', 'cloud', 'aws', 'gcp'], icon: BookOpen },
    { label: 'Manager', filter: 'manager', tags: ['Manager', 'Product Manager'], icon: TrendingUp },
    { label: 'Data Science', filter: 'data-science', tags: ['Data', 'Science', 'Analyst', 'datalake', 'bigdata'], icon: Sparkles },
    { label: 'Hardware', filter: 'hardware', tags: ['Hardware'], icon: Shield },
    { label: 'Human Resources', filter: 'human-resources', tags: ['HR', 'Human Resources'], icon: Users },
  ];
  const popularfilters = [
    { label: 'AI', filter: 'ai', tags: ['ia', 'intelligence', 'artific'], icon: Shield },
    { label: 'python', filter: 'python', tags: ['python','py'], icon: Zap },
    { label: 'internship', filter: 'stage', tags: ['apprenticeship', 'intern', 'junior', 'student'], icon: Briefcase },
    { label: 'manager', filter: 'manager', tags: ['Manager', 'Product Manager'], icon: TrendingUp },
    { label: 'Data Science', filter: 'data-science', tags: ['Data Science', 'Data Analyst'], icon: Sparkles },
    { label: 'cybersecurity', filter: 'cybersecurity', tags: ['cyber', 'security', 'encryption', 'sec'], icon: Users },
  ];

  // Compute job count by category
  const jobsByCategory = useMemo(() => {
    if (!jobs) return {};
    const counts: any= {};
    categories.forEach(cat => {
      const found = jobs.filter(job =>
        cat.tags.some(tag => job.tags.includes(tag) 
          || job.tags.includes(cat.filter) 
          || job.title.includes(tag)
          // || job.title.includes(cat.filter)
          // || job.description?.includes(cat.filter)
        ) );
      counts[cat.filter] = found.length;
    });
    return counts;
  }, [jobs]);


  const handleSearch = () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      // Send analytics event for search
      ReactGA.event({
        category: 'Search',
        action: 'form_submitted-homepage',
        label: trimmedQuery,
        value: trimmedQuery.length // (optional)
      });
      navigate(`/jobs?search=${encodeURIComponent(trimmedQuery)}`);
    }
  };

  (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };


  // Use jobs from context or fallback to empty array while loading
  const featuredJobs = jobs===null ? []: jobs.splice(0,4) ;


  return (
    <>
      <SEO
        title="PACA-dev tech job board in South of France"
        description="Discover top tech companies hiring in Sophia Antipolis, Nice, Cannes, and Monaco. Find your dream employer in the French Riviera tech ecosystem."
        keywords="sophia antipolis companies, tech companies nice, french riviera employers, amadeus cisco sap jobs"
      />
      <div className="min-h-screen">
        

        {/* Hero Section with Animated Background */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 text-white py-20 overflow-hidden">
          {/* Animated Background Elements */}
            <div className=" absolute inset-0 overflow-hidden">
              <div className="flex justify-center p-4">
                <div  style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                </div>
              </div>
              
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
            <div className="absolute top-40 left-1/2 w-60 h-60 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">

              <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-tight">
                { t.heroTitle || "Job board in"} 
                <span className="block  animate-gradient">
                  South of France
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
                {t.heroSubtitle || "Discover tech opportunities on the French Riviera (PACA region)."}
              </p>
              
              {/* Enhanced Search Bar */}
              <div className="max-w-3xl mx-auto mb-12">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-teal-400 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
                  <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl p-2 shadow-2xl">
                    <div className="flex items-center">
                      <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t.searchPlaceholder || "Search for jobs, companies, or skills..." } 
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 text-gray-900 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                        />
                      </div>
                      <button 
                        className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                        onClick={()=>handleSearch()}
                        >
                        {t.searchButton || "Search Jobs"}
                      </button>
                    </div>

                  </div>
                </div>
                    
                    {/* Popular Filters as Pills */}
                    <div className="flex flex-wrap gap-3 mt-4 justify-center">
                      {popularfilters.map(cat => (
                        <button
                          key={cat.filter}
                          className="px-4 py-2 rounded-full bg-blue-50 text-blue-700 font-medium border transition hover:bg-blue-100 hover:shadow"
                          onClick={() => navigate(`/jobs?search=${cat.filter + ' ' + cat.tags.join(' ')}`)}
                        >
                          {cat.label}
                          
                        </button>
                      ))}
                    </div>
              </div>

              {/* CTA Buttons */}
              {/* <div className="flex flex-wrap justify-center gap-6 mb-12">
                <Link
                  to="/jobs"
                  className="group bg-white text-blue-800 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center"
                >
                  <Briefcase className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" />
                  Browse All Jobs
                </Link>
                
              </div> */}

            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Categories</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {categories.map(cat => (
                <div
                  key={cat.filter}
                  className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:shadow-md transition"
                  onClick={() => navigate(`/jobs?search=${cat.filter +' '+cat.tags.join(' ')}`)}
                >
                  <cat.icon className="h-8 w-8 mb-3 text-blue-600" />
                  <div className="font-semibold text-lg text-blue-800">{cat.label}</div>
                  <div className="mt-2 text-sm text-blue-700">
                    {jobsByCategory[cat.filter]} positions
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Newsletter */}
        <section>
          <NewsletterSubscription />
        </section>

        {/* Job Calendar Heatmap */}
        {/* <section className="border-t border-gray-200">
              <JobCalendarHeatmap jobs={jobs || []} weeks={12} />
        </section> */}

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-lg mb-4">
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Featured Jobs */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Opportunities</h2>
              <p className="text-lg text-gray-600">Hand-picked jobs from top tech companies in the region</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {featuredJobs.map((job) => (
                <div key={job.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{job.title}</h3>
                      <p className="text-blue-600 font-medium">{job.company}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      job.type === 'Full-time' ? 'bg-green-100 text-green-800' :
                      job.type === 'Internship' ? 'bg-purple-100 text-purple-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {job.type}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span className="text-sm">{job.location}</span>
                    {job.remote && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Remote OK</span>
                    )}
                  </div>
                  
                  <div className="text-green-600 font-semibold mb-4">{job.salary}</div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {job.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link
                    onClick={()=>{
                      ReactGA.event({
                        category: 'job_view',
                        action: 'open_job_details-'+job.company,
                        label: "open_job",
                        value: 1 // (optional)
                      });
                    }}
                    to={`/job/${job.id}`}
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                  >
                    View Details <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <Link
                to="/jobs"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View All Jobs
              </Link>
            </div>
          </div>
        </section>

        <CVUploadModal 
          isOpen={isCVModalOpen} 
          onClose={() => setIsCVModalOpen(false)}
          onSuccess={() => {
            // Redirect to jobs page or dashboard after successful upload
            setTimeout(() => {
              window.location.href = user ? '/#/dashboard' : '/#/jobs';
            }, 2000);
          }}
        />
    
      </div>
    </>

    
  );
};

export default Home;